import type { Session } from '@mesa/core/auth/better-auth';
import {
  CaretUpIcon,
  CheckIcon,
  CircleHalfTiltIcon,
  MoonIcon,
  SignOutIcon,
  SunIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { broadcastAuthChange } from '@/lib/auth-broadcast';
import { pushEvent, resetSession } from '@/lib/faro';
import { signOutServerFn } from '@/server/functions/auth';

interface UserMenuProps {
  session: Session;
}

export function UserMenu({ session }: UserMenuProps) {
  const navigate = useNavigate();
  const { orgSlug } = useParams({ strict: false }) as { orgSlug?: string };
  const { theme, setTheme } = useTheme();
  const { isCollapsed } = useSidebar();
  const queryClient = useQueryClient();
  const user = session.user;
  const isStaff = (user as { role?: string }).role === 'mesa_staff';

  const handleSignOut = async () => {
    pushEvent('user-sign-out', { source: 'user-menu' });
    resetSession();
    await signOutServerFn();
    queryClient.clear();
    broadcastAuthChange();
    navigate({ to: '/' });
  };

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
    : user.email.charAt(0).toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={user.image ?? undefined} alt={user.name ?? user.email} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.name ?? 'User'}</span>
                    <span className="truncate text-xs text-mesa-muted">{user.email}</span>
                  </div>
                  <CaretUpIcon className="ml-auto" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[--sidebar-width] min-w-56" align="end" sideOffset={4}>
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.image ?? undefined} alt={user.name ?? user.email} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name ?? 'User'}</span>
                  <span className="truncate text-xs text-mesa-muted">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Theme</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setTheme('light')}>
                    <SunIcon />
                    Light
                    {theme === 'light' && <CheckIcon className="ml-auto" size={12} weight="bold" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('dark')}>
                    <MoonIcon />
                    Dark
                    {theme === 'dark' && <CheckIcon className="ml-auto" size={12} weight="bold" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')}>
                    <CircleHalfTiltIcon />
                    System
                    {theme === 'system' && <CheckIcon className="ml-auto" size={12} weight="bold" />}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            {isStaff && orgSlug && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => navigate({ to: `/${orgSlug}/admin/impersonate` })}
                >
                  <UsersIcon className="mr-2" />
                  Impersonate
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <SignOutIcon className="mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
